import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Leaf } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const [searchParams] = useSearchParams();
  // Tester deep-link: /register?promo=LALAFAM2026&email=tester@example.com
  const promoFromUrl = searchParams.get("promo") || "";
  const emailFromUrl = searchParams.get("email") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);

      // Tester deep-link path: auto-redeem the promo code so the user is
      // upgraded to lifetime free access before they see the quiz.
      if (promoFromUrl) {
        try {
          const token = localStorage.getItem("token");
          await axios.post(
            `${API}/promo/redeem`,
            { code: promoFromUrl },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success("Welcome! Your lifetime tester access is unlocked. 🌳");
        } catch (err) {
          // Non-blocking — they still got an account, just couldn't redeem
          toast.message("Account created. Promo code couldn't be applied — you can redeem it on the Subscription page.");
        }
      } else {
        toast.success("Welcome to Lala Land! Let's personalize your journey.");
      }

      // Redirect new users to quiz instead of dashboard
      navigate("/quiz");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <span className="text-xl text-primary font-semibold" style={{ fontFamily: "'Righteous', cursive" }}>Lala Land</span>
          </Link>

          <div className="mb-8">
            <h1 className="text-4xl text-primary mb-2">Begin Your Journey</h1>
            <p className="text-muted-foreground">Create your account to access spiritual tools</p>
          </div>

          {promoFromUrl && (
            <div
              className="mb-6 p-4 rounded-xl border-2 border-dashed border-emerald-500/40 bg-emerald-500/5"
              data-testid="register-promo-banner"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">🌳</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                    Tester invite detected
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Your code <code className="px-1.5 py-0.5 bg-emerald-500/10 rounded text-emerald-700 dark:text-emerald-300 font-mono">{promoFromUrl}</code> will unlock lifetime free access right after sign-up. No card needed.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-12 bg-muted/50 border-border focus:border-primary"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-muted/50 border-border focus:border-primary"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 bg-muted/50 border-border focus:border-primary pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary text-primary-foreground rounded-full hover:bg-primary/90"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Leaf className="h-4 w-4 animate-spin" />
                  Creating account...
                </span>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
          <p className="mt-4 text-center text-sm text-muted-foreground italic">Stay Rooted 🌳</p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1676666372076-8159112de234?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-primary/30"></div>
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <h3 className="text-2xl text-white mb-4">What's included:</h3>
            <ul className="space-y-3 text-white/90">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-accent rounded-full"></span>
                Daily affirmations for your mindset
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-accent rounded-full"></span>
                Angel number meanings & guidance
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-accent rounded-full"></span>
                Transformative journaling space
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-accent rounded-full"></span>
                Sound Sanctuary (Jazz, Soul, 432Hz Healing)
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
